import { useState, useEffect, useMemo } from 'react';
import Modal from '@mui/material/Modal';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';
import { toast } from 'react-toastify';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EastIcon from '@mui/icons-material/East';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ViewInArOutlinedIcon from '@mui/icons-material/ViewInArOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import {
  useGetLayerListDropdownByCube,
  useGetCubeTimeInterval,
  useGetCubeTimePeriodDropdown,
} from 'services/v1/Tenant/AROCube/CubeDataEntryService';
import { useGetFunctionDefinitionCubeDropdown } from 'services/v1/SystemTenant/AROFunction/FunctionDefinitionService';
import { useGetInsightViewCubeAttributesDropdown } from 'services/v1/Tenant/AROCube/CubeInsightViewService';
import { useGetFunctionDefinitionSubjectAttributeDropdown } from 'services/v1/SystemTenant/AROFunction/FunctionDefinitionService';
import { useGetSubjectDetails } from 'services/v1/SystemTenant/AROKMS/SubjectService';
import { RULE_TYPE_PRICE_FROM_SUBJECT, RULE_TYPE_NUMBER_FROM_SUBJECT } from 'constant/RuleConstant';
import {
  CubeToSubjectDefinition,
  RequestCubeToSubjectDefinition,
  useGetCubeToSubjectDefinitions,
  useCreateCubeToSubjectDefinition,
  useUpdateCubeToSubjectDefinition,
  useDeleteCubeToSubjectDefinition,
} from 'services/v1/Tenant/AROCube/CubeToSubjectDefinitionService';

// Prefilled selections carried over from the Decision Point definition form.
export interface DataFlowDraft {
  cube?: AutoCompleteItem | null;
  layer?: AutoCompleteItem | null;
  timeInterval?: AutoCompleteItem | null;
  measure?: AutoCompleteItem | null;
  timePeriod?: AutoCompleteItem | null;
  tableDefinition?: AutoCompleteItem | null;
}

interface ModalDataFlowDefinitionProps {
  open: boolean;
  onClose: () => void;
  targetSubjectId?: string;
  initialDraft?: DataFlowDraft;
}

const modalContentStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 760,
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
  outline: 'none',
};

const filterAutoCompleteStyle = {
  flex: 1,
  '& .MuiOutlinedInput-root': { padding: '1px 2px' },
};

const sectionBoxStyle = {
  bgcolor: '#FBFBFB',
  py: 1.5,
  px: 1.5,
  borderRadius: 1,
  border: '1px solid #E3EBF6',
};

const stepBadgeStyle = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  bgcolor: '#3B4797',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const NUMERIC_ATTRIBUTE_RULE_TYPES = new Set<string>([
  ...RULE_TYPE_PRICE_FROM_SUBJECT,
  ...RULE_TYPE_NUMBER_FROM_SUBJECT,
]);

const isOptionEqual = (option: AutoCompleteItem, value: AutoCompleteItem) =>
  option.value?.toString() === value.value?.toString();

// Renders a selection value inside the definition summary sentence.
function SummaryToken(props: { label?: string | null }) {
  const { label } = props;
  if (!label) {
    return (
      <Box component='span' sx={{ fontStyle: 'italic', color: '#98A2AE' }}>
        not selected
      </Box>
    );
  }
  return (
    <Box component='span' sx={{ fontWeight: 700, color: '#3B4797' }}>
      {label}
    </Box>
  );
}

export function ModalDataFlowDefinition(props: Readonly<ModalDataFlowDefinitionProps>) {
  const { open, onClose, targetSubjectId, initialDraft } = props;

  const [view, setView] = useState<'list' | 'form'>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // ── Form state ──
  const [name, setName] = useState('');
  const [cube, setCube] = useState<AutoCompleteItem | null>(null);
  const [layer, setLayer] = useState<AutoCompleteItem | null>(null);
  const [timeInterval, setTimeInterval] = useState<AutoCompleteItem | null>(null);
  const [measure, setMeasure] = useState<AutoCompleteItem | null>(null);
  const [timePeriod, setTimePeriod] = useState<AutoCompleteItem | null>(null);
  const [tableDefinition, setTableDefinition] = useState<AutoCompleteItem | null>(null);
  // Raw definition currently being resolved into the form (edit mode).
  const [pendingEdit, setPendingEdit] = useState<CubeToSubjectDefinition | null>(null);

  // ── Data hooks ──
  const { data: subjectDetails } = useGetSubjectDetails(open ? targetSubjectId : undefined);
  const { data: definitionsResponse, isFetching: isFetchingList } = useGetCubeToSubjectDefinitions(open);
  const { data: cubeDropdown, isLoading: isFetchingCube } = useGetFunctionDefinitionCubeDropdown();
  const { data: layerDropdown, isLoading: isFetchingLayer } = useGetLayerListDropdownByCube(cube?.value);
  const { data: timeIntervalDropdown, isLoading: isFetchingInterval } = useGetCubeTimeInterval(cube?.value);
  const { data: measureDropdown, isLoading: isFetchingMeasure } = useGetInsightViewCubeAttributesDropdown(
    cube?.value?.toString(),
  );
  const { data: timePeriodDropdown, isLoading: isFetchingTimePeriod } = useGetCubeTimePeriodDropdown(
    cube?.value?.toString(),
    timeInterval?.value?.toString(),
  );
  const { data: tableDefinitionDropdown, isLoading: isFetchingTableDef } =
    useGetFunctionDefinitionSubjectAttributeDropdown(targetSubjectId ?? null);

  const { mutate: createDefinition, isLoading: isCreating } = useCreateCubeToSubjectDefinition();
  const { mutate: updateDefinition, isLoading: isUpdating } = useUpdateCubeToSubjectDefinition();
  const { mutate: deleteDefinition, isLoading: isDeleting } = useDeleteCubeToSubjectDefinition();

  const targetSubjectLabel = subjectDetails?.data?.displayName || subjectDetails?.data?.subject || 'this table';

  // ── Derived options ──
  const cubeOptions: AutoCompleteItem[] = useMemo(() => cubeDropdown?.data?.data ?? [], [cubeDropdown?.data]);
  const layerOptions: AutoCompleteItem[] = useMemo(() => layerDropdown?.data ?? [], [layerDropdown?.data]);
  const timeIntervalOptions: AutoCompleteItem[] = useMemo(
    () => timeIntervalDropdown?.data ?? [],
    [timeIntervalDropdown?.data],
  );
  const measureOptions: AutoCompleteItem[] = useMemo(() => measureDropdown?.data ?? [], [measureDropdown?.data]);
  const timePeriodOptions: AutoCompleteItem[] = useMemo(
    () => timePeriodDropdown?.data ?? [],
    [timePeriodDropdown?.data],
  );
  const tableDefinitionOptions: AutoCompleteItem[] = useMemo(
    () => (tableDefinitionDropdown?.data ?? []).filter((item) => NUMERIC_ATTRIBUTE_RULE_TYPES.has(item.type)),
    [tableDefinitionDropdown?.data],
  );

  // Only data flows targeting this table's subject.
  const definitions: CubeToSubjectDefinition[] = useMemo(
    () => (definitionsResponse?.data ?? []).filter((d) => !targetSubjectId || d.target.subjectId === targetSubjectId),
    [definitionsResponse?.data, targetSubjectId],
  );

  const tableDefinitionLabelById = useMemo(() => {
    const map = new Map<string, string>();
    tableDefinitionOptions.forEach((item) => map.set(item.value.toString(), item.label));
    return map;
  }, [tableDefinitionOptions]);

  const resetForm = () => {
    setName('');
    setCube(null);
    setLayer(null);
    setTimeInterval(null);
    setMeasure(null);
    setTimePeriod(null);
    setTableDefinition(null);
    setPendingEdit(null);
    setEditingId(null);
  };

  // Reset to list view whenever the modal closes.
  useEffect(() => {
    if (!open) {
      setView('list');
      setConfirmDeleteId(null);
      resetForm();
    }
  }, [open]);

  // ── Edit-mode resolution: fill the form from a definition as option lists load ──
  useEffect(() => {
    if (!pendingEdit) return;
    if (!cube) {
      const match = cubeOptions.find((o) => o.value?.toString() === pendingEdit.source.cubeDefinitionId);
      if (match) setCube(match);
    }
  }, [pendingEdit, cubeOptions, cube]);

  useEffect(() => {
    if (!pendingEdit || layer) return;
    const match = layerOptions.find((o) => o.value?.toString() === pendingEdit.source.cubeLayerId);
    if (match) setLayer(match);
  }, [pendingEdit, layerOptions, layer]);

  useEffect(() => {
    if (!pendingEdit || timeInterval) return;
    const match = timeIntervalOptions.find((o) => o.value?.toString() === pendingEdit.source.timeInterval);
    if (match) setTimeInterval(match);
  }, [pendingEdit, timeIntervalOptions, timeInterval]);

  useEffect(() => {
    if (!pendingEdit || measure) return;
    const match = measureOptions.find((o) => o.value?.toString() === pendingEdit.source.cubeAttributeId);
    if (match) setMeasure(match);
  }, [pendingEdit, measureOptions, measure]);

  useEffect(() => {
    if (!pendingEdit || timePeriod) return;
    const match = timePeriodOptions.find((o) => o.value?.toString() === pendingEdit.source.timePeriodValue);
    if (match) setTimePeriod(match);
  }, [pendingEdit, timePeriodOptions, timePeriod]);

  useEffect(() => {
    if (!pendingEdit || tableDefinition) return;
    const match = tableDefinitionOptions.find((o) => o.value?.toString() === pendingEdit.target.tableDefinitionId);
    if (match) setTableDefinition(match);
  }, [pendingEdit, tableDefinitionOptions, tableDefinition]);

  const handleCubeChange = (_: React.SyntheticEvent, value: AutoCompleteItem | null) => {
    setCube(value);
    setLayer(null);
    setTimeInterval(null);
    setMeasure(null);
    setTimePeriod(null);
    // User is driving the cascade now: stop auto-resolving the edited definition.
    setPendingEdit(null);
  };

  const openNewForm = () => {
    resetForm();
    if (initialDraft) {
      setCube(initialDraft.cube ?? null);
      setLayer(initialDraft.layer ?? null);
      setTimeInterval(initialDraft.timeInterval ?? null);
      setMeasure(initialDraft.measure ?? null);
      setTimePeriod(initialDraft.timePeriod ?? null);
      setTableDefinition(initialDraft.tableDefinition ?? null);
    }
    setView('form');
  };

  const openEditForm = (definition: CubeToSubjectDefinition) => {
    resetForm();
    setEditingId(definition.id);
    setName(definition.name);
    setPendingEdit(definition);
    setView('form');
  };

  const backToList = () => {
    resetForm();
    setView('list');
  };

  const isFormValid = Boolean(
    name.trim() && cube && layer && timeInterval && measure && timePeriod && tableDefinition && targetSubjectId,
  );

  const handleSave = () => {
    if (!isFormValid) return;
    const body: RequestCubeToSubjectDefinition = {
      name: name.trim(),
      status: 'ACTIVE',
      target: {
        subjectId: targetSubjectId as string,
        tableDefinitionId: tableDefinition!.value.toString(),
      },
      source: {
        cubeAttributeId: measure!.value.toString(),
        cubeDefinitionId: cube!.value.toString(),
        cubeLayerId: layer!.value.toString(),
        timeInterval: timeInterval!.value.toString(),
        timePeriodValue: timePeriod!.value.toString(),
      },
    };
    const onSuccess = () => {
      toast.success(editingId ? 'Data flow updated' : 'Data flow created');
      backToList();
    };
    const onError = () => {
      toast.error('Failed to save data flow');
    };
    if (editingId) {
      updateDefinition({ definitionId: editingId, body }, { onSuccess, onError });
    } else {
      createDefinition(body, { onSuccess, onError });
    }
  };

  const handleDelete = (definitionId: string) => {
    deleteDefinition(
      { definitionId },
      {
        onSuccess: () => {
          toast.success('Data flow removed');
          setConfirmDeleteId(null);
        },
        onError: () => {
          toast.error('Failed to remove data flow');
        },
      },
    );
  };

  const isSaving = isCreating || isUpdating;

  return (
    <Modal open={open} onClose={onClose}>
      <Stack sx={modalContentStyle} spacing={2}>
        {/* ── Header ── */}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Stack direction='row' alignItems='center' spacing={1.25}>
            {view === 'form' && (
              <IconButton size='small' onClick={backToList}>
                <ArrowBackRoundedIcon sx={{ color: '#3B4797' }} />
              </IconButton>
            )}
            <AccountTreeOutlinedIcon sx={{ color: '#42BB93' }} />
            <Stack spacing={0}>
              <Typography variant='body1' fontWeight='bold' sx={{ color: '#3B4797', lineHeight: 1.2 }}>
                {view === 'list' ? 'Data Flows' : editingId ? 'Edit Data Flow' : 'New Data Flow'}
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                {view === 'list'
                  ? `Update ${targetSubjectLabel} with the latest cube figures`
                  : `Reusable cube to ${targetSubjectLabel} calculation`}
              </Typography>
            </Stack>
          </Stack>
          <IconButton size='small' onClick={onClose}>
            <HighlightOffRoundedIcon sx={{ color: '#98A2AE' }} />
          </IconButton>
        </Stack>
        <Divider />

        {view === 'list' ? (
          <>
            <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
              <Typography variant='body2' sx={{ color: '#475467' }}>
                Press <b>Run</b> on a data flow to pull the latest cube figures into {targetSubjectLabel}.
              </Typography>
              <Button
                variant='main-table-panel-border'
                startIcon={<AddCircleOutlineOutlinedIcon />}
                onClick={openNewForm}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                New Data Flow
              </Button>
            </Stack>

            {isFetchingList ? (
              <Stack spacing={1}>
                <Skeleton variant='rounded' height={64} />
                <Skeleton variant='rounded' height={64} />
              </Stack>
            ) : definitions.length === 0 ? (
              <Stack alignItems='center' spacing={0.75} sx={{ py: 5 }}>
                <AccountTreeOutlinedIcon sx={{ fontSize: 40, color: '#C8D7EC' }} />
                <Typography variant='body2' fontWeight={600} sx={{ color: '#475467' }}>
                  No data flows yet
                </Typography>
                <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                  Create one to define how cube data updates {targetSubjectLabel}.
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1}>
                {definitions.map((definition) => {
                  const columnLabel =
                    tableDefinitionLabelById.get(definition.target.tableDefinitionId) || 'a column';
                  const isConfirming = confirmDeleteId === definition.id;
                  return (
                    <Stack
                      key={definition.id}
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                      spacing={1}
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        bgcolor: '#fff',
                        border: '1px solid #E3EBF6',
                        borderRadius: 1,
                        transition: 'border-color 150ms ease, box-shadow 150ms ease',
                        '&:hover': { borderColor: '#C8D7EC', boxShadow: '0 1px 4px rgba(16, 24, 40, 0.06)' },
                      }}
                    >
                      <Stack spacing={0.5} flex={1} minWidth={0}>
                        <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797' }} noWrap>
                          {definition.name}
                        </Typography>
                        <Stack direction='row' alignItems='center' spacing={0.75} flexWrap='wrap'>
                          <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                            {definition.source.timeInterval} · {definition.source.timePeriodValue}
                          </Typography>
                          <EastIcon sx={{ fontSize: 13, color: '#C8D7EC' }} />
                          <Typography variant='caption' sx={{ color: '#475467', fontWeight: 600 }}>
                            {columnLabel}
                          </Typography>
                        </Stack>
                      </Stack>
                      {isConfirming ? (
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Typography variant='caption' sx={{ color: '#98A2AE' }}>
                            Remove?
                          </Typography>
                          <Button
                            size='small'
                            variant='main-table-panel-border'
                            onClick={() => setConfirmDeleteId(null)}
                            sx={{ textTransform: 'none' }}
                          >
                            Cancel
                          </Button>
                          <ButtonLoading
                            size='small'
                            variant='outlined'
                            color='error'
                            loading={isDeleting}
                            onClick={() => handleDelete(definition.id)}
                            sx={{ textTransform: 'none' }}
                          >
                            Remove
                          </ButtonLoading>
                        </Stack>
                      ) : (
                        <Stack direction='row' alignItems='center'>
                          <Tooltip title='Edit'>
                            <IconButton size='small' onClick={() => openEditForm(definition)}>
                              <EditIcon fontSize='small' sx={{ color: '#3B4797' }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title='Remove'>
                            <IconButton size='small' onClick={() => setConfirmDeleteId(definition.id)}>
                              <DeleteOutlineRoundedIcon fontSize='small' sx={{ color: '#D92D20' }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </>
        ) : (
          <>
            {/* ── Purpose banner ── */}
            <Stack
              direction='row'
              spacing={1.25}
              sx={{
                p: 1.5,
                bgcolor: 'rgba(66, 187, 147, 0.06)',
                border: '1px solid rgba(66, 187, 147, 0.3)',
                borderRadius: 1,
              }}
            >
              <InfoOutlinedIcon fontSize='small' sx={{ color: '#42BB93', mt: '1px' }} />
              <Typography variant='body2' sx={{ color: '#475467' }}>
                Name this data flow, pick the cube measure and period to read from, and the column of{' '}
                <Box component='span' sx={{ fontWeight: 700, color: '#3B4797' }}>
                  {targetSubjectLabel}
                </Box>{' '}
                it updates.
              </Typography>
            </Stack>

            <TextField
              size='small'
              label='Name'
              placeholder='e.g. FY26 revenue to account revenue'
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />

            {/* ── Source ── */}
            <Stack spacing={1.5} sx={sectionBoxStyle}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Box sx={stepBadgeStyle}>1</Box>
                <ViewInArOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
                <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797' }}>
                  Source
                </Typography>
              </Stack>
              <Stack direction='row' spacing={2}>
                <Autocomplete
                  size='small'
                  sx={filterAutoCompleteStyle}
                  options={cubeOptions}
                  value={cube}
                  onChange={handleCubeChange}
                  getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
                  isOptionEqualToValue={isOptionEqual}
                  clearIcon={null}
                  renderInput={(params) => <TextField {...params} label={isFetchingCube ? 'Loading...' : 'Cube'} />}
                />
                <Autocomplete
                  size='small'
                  sx={filterAutoCompleteStyle}
                  options={layerOptions}
                  value={layer}
                  onChange={(_, value) => setLayer(value)}
                  getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
                  isOptionEqualToValue={isOptionEqual}
                  disabled={!cube}
                  clearIcon={null}
                  renderInput={(params) => <TextField {...params} label={isFetchingLayer ? 'Loading...' : 'Layer'} />}
                />
              </Stack>
              <Stack direction='row' spacing={2}>
                <Autocomplete
                  size='small'
                  sx={filterAutoCompleteStyle}
                  options={measureOptions}
                  value={measure}
                  onChange={(_, value) => setMeasure(value)}
                  getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
                  isOptionEqualToValue={isOptionEqual}
                  disabled={!cube}
                  clearIcon={null}
                  renderInput={(params) => (
                    <TextField {...params} label={isFetchingMeasure ? 'Loading...' : 'Measure'} />
                  )}
                />
                <Autocomplete
                  size='small'
                  sx={filterAutoCompleteStyle}
                  options={timeIntervalOptions}
                  value={timeInterval}
                  onChange={(_, value) => {
                    setTimeInterval(value);
                    // Time periods depend on the interval, so reset the selection.
                    setTimePeriod(null);
                  }}
                  getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
                  isOptionEqualToValue={isOptionEqual}
                  disabled={!cube}
                  clearIcon={null}
                  renderInput={(params) => (
                    <TextField {...params} label={isFetchingInterval ? 'Loading...' : 'Time Interval'} />
                  )}
                />
                <Autocomplete
                  size='small'
                  sx={filterAutoCompleteStyle}
                  options={timePeriodOptions}
                  value={timePeriod}
                  onChange={(_, value) => setTimePeriod(value)}
                  getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
                  isOptionEqualToValue={isOptionEqual}
                  disabled={!cube || !timeInterval}
                  clearIcon={null}
                  renderInput={(params) => (
                    <TextField {...params} label={isFetchingTimePeriod ? 'Loading...' : 'Time Period'} />
                  )}
                />
              </Stack>
            </Stack>

            {/* ── Target ── */}
            <Stack spacing={1.5} sx={sectionBoxStyle}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Box sx={stepBadgeStyle}>2</Box>
                <TableChartOutlinedIcon fontSize='small' sx={{ color: '#3B4797' }} />
                <Typography variant='body2' fontWeight={700} sx={{ color: '#3B4797' }}>
                  Target
                </Typography>
              </Stack>
              <Stack direction='row' spacing={2} alignItems='center'>
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={0.75}
                  sx={{ px: 1.25, py: 0.75, bgcolor: '#EDF2FF', borderRadius: 1, flexShrink: 0 }}
                >
                  <TableChartOutlinedIcon sx={{ fontSize: 16, color: '#3B4797' }} />
                  <Typography variant='body2' sx={{ color: '#3B4797', fontWeight: 600 }}>
                    {targetSubjectLabel}
                  </Typography>
                </Stack>
                <Autocomplete
                  size='small'
                  sx={filterAutoCompleteStyle}
                  options={tableDefinitionOptions}
                  value={tableDefinition}
                  onChange={(_, value) => setTableDefinition(value)}
                  getOptionLabel={(option: AutoCompleteItem) => option.label || ''}
                  isOptionEqualToValue={isOptionEqual}
                  clearIcon={null}
                  renderInput={(params) => (
                    <TextField {...params} label={isFetchingTableDef ? 'Loading...' : 'Column to Update'} />
                  )}
                />
              </Stack>
            </Stack>

            {/* ── Definition summary ── */}
            <Box sx={{ px: 1.5, py: 1.25, border: '1px dashed #C8D7EC', borderRadius: 1 }}>
              <Typography
                variant='caption'
                sx={{ color: '#98A2AE', fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}
              >
                Definition Summary
              </Typography>
              <Typography variant='body2' sx={{ color: '#475467', mt: 0.25 }}>
                <SummaryToken label={name.trim() || null} /> updates column{' '}
                <SummaryToken label={tableDefinition?.label} /> in <SummaryToken label={targetSubjectLabel} /> with
                measure <SummaryToken label={measure?.label} /> from cube <SummaryToken label={cube?.label} /> (layer{' '}
                <SummaryToken label={layer?.label} />, <SummaryToken label={timeInterval?.label} /> period{' '}
                <SummaryToken label={timePeriod?.label} />).
              </Typography>
            </Box>
          </>
        )}

        <Divider />
        <Stack direction='row' spacing={1.5} justifyContent='flex-end'>
          {view === 'form' ? (
            <>
              <Button variant='main-table-panel-border' onClick={backToList} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <ButtonLoading
                variant='main-table-panel'
                loading={isSaving}
                disabled={!isFormValid || isSaving}
                onClick={handleSave}
                sx={{ textTransform: 'none' }}
              >
                {editingId ? 'Update Data Flow' : 'Create Data Flow'}
              </ButtonLoading>
            </>
          ) : (
            <Button variant='main-table-panel-border' onClick={onClose} sx={{ textTransform: 'none' }}>
              Close
            </Button>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}
